<template>
  <div class="patient-sms-panel">
    <!-- SMS Header -->
    <div class="sms-header">
      <div class="patient-info">
        <div class="patient-avatar">
          <i class="fa fa-user"></i>
        </div>
        <div class="patient-name">{{ patient.Name || patient.PatientName }}</div>
        <div class="patient-phone" v-if="patient.WirelessPhone">
          <i class="fa fa-mobile"></i>
          <span>{{ formatPhone(patient.WirelessPhone) }}</span>
        </div>
        <div class="outgoing-sms-info" v-if="outgoingSmsNumber">
          <i class="fa fa-phone"></i>
          <span>{{ siteName }}'s outgoing SMS: <strong>{{ outgoingSmsNumber }}</strong></span>
        </div>
      </div>
      <div class="header-actions">
        <button class="action-btn" @click="$emit('minimize')" title="Minimize">
          <i class="fa fa-minus"></i>
        </button>
        <button class="action-btn" @click="$emit('close')" title="Close">
          <i class="fa fa-times"></i>
        </button>
      </div>
    </div>

    <!-- GROUP MMS WARNING BANNER -->
    <!-- This appears when the conversation is a Group MMS (patient texted multiple recipients) -->
    <div v-if="conversation && conversation.isGroupMMS" class="group-mms-warning">
      <i class="fa fa-exclamation-triangle"></i>
      <div class="warning-content">
        <strong>Group Conversation</strong>
        <p>This patient texted multiple recipients. Your replies will be visible to all participants in the group.</p>
      </div>
    </div>

    <!-- Messages Area -->
    <div class="messages-container" ref="messagesContainer">
      <div v-if="!messagesLoaded" class="loading-messages">
        <i class="fa fa-spinner fa-spin"></i>
        <span>Loading SMS messages...</span>
      </div>
      
      <div v-else-if="!conversation" class="no-conversation">
        <div class="no-conversation-content">
          <i class="fa fa-mobile-phone"></i>
          <h4>No SMS conversation yet</h4>
          <p>Start texting with {{ patient.Name || patient.PatientName }} by sending a message below.</p>
        </div>
      </div>
      
      <div v-else class="messages-list">
        <div 
          v-for="message in messages" 
          :key="message._id || message.id"
          class="message"
          :class="{ 
            'outbound': isOutboundMessage(message), 
            'inbound': !isOutboundMessage(message),
            'message-failed': isFailedMessage(message),
            'message-pending': isPendingMessage(message)
          }"
        >
          <div class="message-content">
            <div class="message-text" v-html="formatMessageContent(message)"></div>
            <div class="message-meta">
              <span class="message-time" v-if="getMessageTimestamp(message)">{{ formatMessageTime(getMessageTimestamp(message)) }}</span>
              <!-- Failed message indicator with reason -->
              <span v-if="isFailedMessage(message)" class="message-failed-indicator">
                <i class="fa fa-exclamation-circle"></i>
                <span class="failed-text">{{ getFailedReason(message) }}</span>
                <button v-if="!isOptedOutError(message)" class="retry-btn" @click="$emit('retry-message', message)" title="Retry">
                  <i class="fa fa-refresh"></i>
                </button>
              </span>
              <!-- Pending/Sending indicator -->
              <span v-else-if="isPendingMessage(message)" class="message-pending-indicator">
                <i class="fa fa-clock-o"></i>
                <span class="pending-text">Sending...</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Enhanced SMS Input Bar -->
    <EnhancedSmsInputBar
      @send-message="handleSendMessage"
      @send-wiki="handleSendWiki"
      @send-form="handleSendForm"
      @send-file="handleSendFile"
      :disabled="false"
    />
  </div>
</template>

<script>
import EnhancedSmsInputBar from './EnhancedSmsInputBar.vue'
import moment from 'moment-timezone'
import { isNonDeliveryCode } from '@/utils/messaging'

export default {
  name: 'PatientSmsPanel',
  components: {
    EnhancedSmsInputBar
  },
  props: {
    patient: {
      type: Object,
      required: true
    },
    conversation: {
      type: Object,
      default: null
    },
    messages: {
      type: Array,
      default: () => []
    },
    messagesLoaded: {
      type: Boolean,
      default: false
    },
    outgoingSmsNumber: {
      type: String,
      default: ''
    },
    siteName: {
      type: String,
      default: 'Your practice'
    }
  },
  methods: {
    formatPhone(phone) {
      if (!phone) return ''
      // Format as (XXX) XXX-XXXX
      const cleaned = phone.replace(/\D/g, '').replace(/^1/, '')
      if (cleaned.length === 10) {
        return `(${cleaned.slice(0,3)}) ${cleaned.slice(3,6)}-${cleaned.slice(6)}`
      }
      return phone
    },
    
    getMessageTimestamp(message) {
      // Try multiple timestamp fields and return the first valid one
      const possibleTimestamps = [
        message.dateCreated,
        message.timestamp,
        message.date,
        message.createdAt,
        message.date_created,
        message.created_at,
        message.dateUpdated,
        message.date_updated,
        message.updated_at
      ];
      
      for (const timestamp of possibleTimestamps) {
        if (timestamp) {
          return timestamp;
        }
      }
      
      // Debug log to help identify the issue
      if (Object.keys(message).length > 0) {
        console.warn('[PatientSMS] Message missing timestamp, available fields:', Object.keys(message), 'message:', message);
      }
      
      return null;
    },
    
    formatMessageTime(timestamp) {
      if (!timestamp) return ''
      
      try {
        let dateToFormat = timestamp
        
        // Handle Vue reactive wrappers - extract the raw value
        let rawTimestamp = timestamp
        if (timestamp && typeof timestamp === 'object' && timestamp.__ob__) {
          // Vue reactive object - try to extract the raw value
          rawTimestamp = JSON.parse(JSON.stringify(timestamp))
        }
        
        // Handle Firestore Timestamp objects (with or without Vue wrapper)
        if (rawTimestamp && typeof rawTimestamp === 'object' && (rawTimestamp._seconds || rawTimestamp.seconds)) {
          // Convert Firestore Timestamp to JavaScript Date
          const seconds = rawTimestamp._seconds || rawTimestamp.seconds || 0
          const nanoseconds = rawTimestamp._nanoseconds || rawTimestamp.nanoseconds || 0
          dateToFormat = new Date(seconds * 1000 + nanoseconds / 1000000)
        } else {
          dateToFormat = rawTimestamp
        }
        
        const momentDate = moment(dateToFormat)
        
        // Validate the moment date
        if (!momentDate.isValid()) {
          console.warn('Invalid timestamp format:', timestamp, 'raw:', rawTimestamp)
          return ''
        }
        
        const now = moment()
        
        if (momentDate.isSame(now, 'day')) {
          return momentDate.format('h:mm A')
        } else if (momentDate.isSame(now.subtract(1, 'day'), 'day')) {
          return 'Yesterday ' + momentDate.format('h:mm A')
        } else {
          return momentDate.format('MMM D, h:mm A')
        }
      } catch (error) {
        console.warn('Error formatting message time:', error, 'timestamp:', timestamp)
        return ''
      }
    },
    
    formatMessageContent(message) {
      const content = message.content || message.body || '';

      // Convert form links with template variables to clickable links
      // Matches: <a href="{{patient_link_for_form_FORM_ID}}">Link Text</a>
      const formLinkRegex = /<a href="{{patient_link_for_form_([^}]+)}}"[^>]*>([^<]+)<\/a>/g;
      let formattedContent = content.replace(formLinkRegex, (_match, formId, linkText) => {
        // Generate the actual preview URL for the form
        const previewUrl = `${window.location.origin}/preview-form/${formId}`;
        return `<a href="${previewUrl}" target="_blank" style="color: inherit; text-decoration: underline; cursor: pointer;">${linkText}</a>`;
      });

      // Also handle the short URL format that comes back from Twilio/backend
      // The backend replaces {{patient_link_for_form_X}} with actual short URLs like https://dev.teamio.app/XYZ
      // These are already plain URLs and will be handled by the URL regex below

      // Convert any other HTML links to be clickable
      const linkRegex = /<a href="([^"]+)"[^>]*>([^<]+)<\/a>/g;
      formattedContent = formattedContent.replace(linkRegex, (match, url, linkText) => {
        return `<a href="${url}" target="_blank" style="color: inherit; text-decoration: underline;">${linkText}</a>`;
      });

      // Convert plain URLs to clickable links (handles https://dev.teamio.app/XYZ format)
      // Only convert URLs that are NOT already inside anchor tags
      // We do this by splitting on existing anchor tags
      const parts = formattedContent.split(/(<a[^>]*>.*?<\/a>)/gi);
      formattedContent = parts.map(part => {
        // If this part is an anchor tag, leave it alone
        if (part.match(/^<a[^>]*>/i)) {
          return part;
        }
        // Otherwise, convert plain URLs to links
        return part.replace(/(https?:\/\/[^\s<>"]+)/g, (url) => {
          return `<a href="${url}" target="_blank" style="color: inherit; text-decoration: underline;">${url}</a>`;
        });
      }).join('');

      // Convert newlines to <br> tags
      formattedContent = formattedContent.replace(/\n/g, '<br>');

      return formattedContent;
    },
    
    isOutboundMessage(message) {
      // Check if message was sent by the site/staff
      // First check explicit attributes
      if (message.attributes?.sentViaAPI || message.attributes?.outbound) {
        return true;
      }
      // Check for inbound attribute (explicitly set on incoming messages)
      if (message.attributes?.inbound) {
        return false;
      }
      // Fallback: Check if author is NOT a phone number (site name = outbound)
      // Outbound messages have author like "Batavia Family Dental", inbound have "+18457026723"
      const author = message.author || '';
      const looksLikePhone = /^\+?\d[\d\s().-]{5,}$/.test(author.trim());
      return !looksLikePhone && author.length > 0;
    },
    
    isFailedMessage(message) {
      // Check if message failed to send
      return message.attributes?.failed === true || message.failed === true;
    },
    
    isPendingMessage(message) {
      // Check if message is still being sent (optimistic, not confirmed)
      return message.attributes?.optimistic === true && !message.attributes?.failed;
    },
    
    getFailedReason(message) {
      // Get the specific reason why message failed
      const reason = message.attributes?.failedReason || message.failedReason;
      if (reason) {
        // Truncate long reasons for display, show first part
        const maxLen = 80;
        if (reason.length > maxLen) {
          return reason.substring(0, maxLen) + '...';
        }
        return reason;
      }
      return 'Failed to send';
    },
    
    isOptedOutError(message) {
      // Hide the retry button for TERMINAL non-deliveries — opt-out AND invalid/
      // undeliverable numbers. Retrying the same message won't help; the number or the
      // patient's "Text OK" must change in Open Dental. (Broadened 2026-07-02.)
      const code = message.attributes?.failedCode || message.failedCode;
      return isNonDeliveryCode(code);
    },
    
    handleSendMessage(messageData) {
      this.$emit('send-message', messageData)
      this.scrollToBottom()
    },
    
    handleSendWiki(wikiData) {
      this.$emit('send-wiki', wikiData)
      this.scrollToBottom()
    },
    
    handleSendForm(formData) {
      this.$emit('send-form', formData)
      this.scrollToBottom()
    },
    
    handleSendFile(fileData) {
      this.$emit('send-file', fileData)
      this.scrollToBottom()
    },
    
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.messagesContainer
        if (container) {
          container.scrollTop = container.scrollHeight
        }
      })
    }
  },
  
  watch: {
    messages: {
      handler() {
        this.scrollToBottom()
      },
      deep: true
    }
  },
  
  mounted() {
    this.scrollToBottom()
  }
}
</script>

<style lang="scss" scoped>
.patient-sms-panel {
  width: 450px;
  height: calc(100vh - 40px);
  background: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  font-family: 'Quicksand', sans-serif;
}

// =====================================================
// GROUP MMS WARNING BANNER
// Shows when patient is in a Group MMS conversation
// =====================================================
.group-mms-warning {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 10px 14px;
  background: linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  border-bottom: 1px solid #fed7aa;
  flex-shrink: 0;

  > i {
    font-size: 1.1rem;
    color: #ea580c;
    flex-shrink: 0;
    margin-top: 2px;
  }

  .warning-content {
    flex: 1;
    min-width: 0;

    strong {
      display: block;
      font-size: 0.8125rem;
      font-weight: 600;
      color: #9a3412;
      margin-bottom: 2px;
    }

    p {
      font-size: 0.75rem;
      color: #c2410c;
      margin: 0;
      line-height: 1.3;
    }
  }
}

.sms-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #1976d2;
  color: white;
  
  .patient-info {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    
    .patient-avatar {
      width: 32px;
      height: 32px;
      border-radius: 50%;
      background: rgba(255,255,255,0.2);
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      
      .fa {
        font-size: 14px;
      }
    }
    
    .patient-name {
      font-weight: 600;
      font-size: 16px;
      line-height: 1.2;
      color: rgba(255, 255, 255, 0.95);
      flex-shrink: 0;
    }
    
    .patient-phone {
      display: flex;
      align-items: center;
      font-size: 12px;
      opacity: 0.85;
      color: rgba(255, 255, 255, 0.85);
      margin-left: auto;

      .fa {
        margin-right: 4px;
        font-size: 11px;
      }
    }

    .outgoing-sms-info {
      display: flex;
      align-items: center;
      font-size: 11px;
      color: rgba(255, 255, 255, 0.75);
      margin-left: 12px;
      padding: 2px 8px;
      background: rgba(255, 255, 255, 0.15);
      border-radius: 12px;

      .fa {
        margin-right: 4px;
        font-size: 10px;
      }
    }
  }
  
  .header-actions {
    display: flex;
    gap: 8px;
    
    .action-btn {
      width: 32px;
      height: 32px;
      border: none;
      background: rgba(255,255,255,0.2);
      color: white;
      border-radius: 6px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: background 0.2s ease;
      
      &:hover {
        background: rgba(255,255,255,0.3);
      }
    }
  }
}

.messages-container {
  flex: 1;
  overflow-y: auto;
  padding: 16px;
  padding-bottom: 24px; // Extra padding at bottom to ensure last message is fully visible
  background: #f5f5f5;
  
  .loading-messages {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    color: #666;
    
    .fa {
      font-size: 24px;
      margin-bottom: 8px;
    }
  }
  
  .no-conversation {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    
    .no-conversation-content {
      text-align: center;
      color: #666;
      
      .fa {
        font-size: 48px;
        margin-bottom: 16px;
        opacity: 0.5;
      }
      
      h4 {
        margin: 0 0 8px 0;
        font-weight: 600;
      }
      
      p {
        margin: 0;
        font-size: 14px;
        line-height: 1.4;
      }
    }
  }
  
  .messages-list {
    .message {
      margin-bottom: 16px;
      
      &.outbound {
        .message-content {
          background: #1976d2;
          color: white;
          margin-left: 40px;
          border-radius: 18px 18px 4px 18px;
          
          .message-time {
            opacity: 0.9; // Better contrast on blue background
            color: rgba(255, 255, 255, 0.9); // Explicit white color
          }
          
          // Make links visible on blue background
          // Using ::v-deep because links are inserted via v-html (scoped CSS doesn't apply to v-html content)
          .message-text ::v-deep a {
            color: white !important;
            text-decoration: underline !important;
          }
        }
      }
      
      &.inbound {
        .message-content {
          background: white;
          color: #333;
          margin-right: 40px;
          border-radius: 18px 18px 18px 4px;
          border: 1px solid #e0e0e0;
        }
      }
      
      .message-content {
        padding: 12px 16px;
        max-width: 80%;
        word-wrap: break-word;
        
        .message-text {
          font-size: 14px;
          line-height: 1.4;
          margin-bottom: 4px;
        }
        
        .message-meta {
          display: flex;
          align-items: center;
          justify-content: flex-end;
          gap: 8px;
          flex-wrap: wrap;
        }
        
        .message-time {
          font-size: 11px;
          opacity: 0.7;
        }
      }
      
      // Failed message styles
      &.message-failed {
        .message-content {
          background: #ffebee !important;
          border: 1px solid #ef5350 !important;
          color: #c62828 !important;
          
          .message-time {
            color: #c62828 !important;
            opacity: 0.8;
          }
        }
        
        .message-failed-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #c62828;
          
          .fa-exclamation-circle {
            color: #ef5350;
          }
          
          .failed-text {
            font-weight: 500;
          }
          
          .retry-btn {
            background: none;
            border: 1px solid #ef5350;
            color: #ef5350;
            padding: 2px 8px;
            border-radius: 12px;
            cursor: pointer;
            font-size: 10px;
            display: flex;
            align-items: center;
            gap: 4px;
            transition: all 0.2s ease;
            
            &:hover {
              background: #ef5350;
              color: white;
            }
          }
        }
      }
      
      // Pending/Sending message styles
      &.message-pending {
        .message-content {
          opacity: 0.7;
          background: #e3f2fd !important;
          border: 1px dashed #90caf9 !important;
        }
        
        .message-pending-indicator {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 11px;
          color: #1976d2;
          
          .fa-clock-o {
            animation: pulse 1.5s ease-in-out infinite;
          }
          
          .pending-text {
            font-style: italic;
          }
        }
      }
    }
  }
}

@keyframes pulse {
  0%, 100% { opacity: 0.5; }
  50% { opacity: 1; }
}

// Mobile responsiveness
@media (max-width: 768px) {
  .patient-sms-panel {
    width: calc(100vw - 30px);
    height: 70vh;
    
    @media (max-width: 480px) {
      width: 100vw;
      height: 100vh;
      border-radius: 0;
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
    }
  }
}

// Custom scrollbar
.messages-container::-webkit-scrollbar {
  width: 6px;
}

.messages-container::-webkit-scrollbar-track {
  background: #f1f1f1;
}

.messages-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 3px;
}

.messages-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}
</style>
